import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Loader2, Bot, Sparkles, Send, MessageSquare,
  Trash2, ArrowRight, Zap, Car, Scale, X, User2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'rvmatch_sales_assistant_chat_v1';

/**
 * WebResearchEmbed — "Sales Assistant" rolling chat for dealers.
 *
 * Persistence: every message (user + AI) is appended to a rolling chat
 * array stored in localStorage so the conversation survives tab switches,
 * dashboard navigation, and full page reloads. The user can clear it
 * explicitly with the Clear button.
 */
const WebResearchEmbed = ({ isDark }) => {
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [activeMode, setActiveMode] = useState('general');
  const [messages, setMessages] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const scrollRef = useRef(null);

  // Hydrate from localStorage on mount (runs once, sets hydrated=true so the
  // persist effect below doesn't clobber localStorage with the initial [].
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setMessages(parsed);
      }
    } catch { /* ignore */ }
    setHydrated(true);
  }, []);

  // Persist to localStorage on every change — but only after hydration so the
  // initial [] doesn't overwrite a saved chat between effect runs (race fix).
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch { /* ignore */ }
  }, [messages, hydrated]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  const sendMessage = async (rawQuery, mode = activeMode) => {
    const query = (rawQuery || '').trim();
    if (!query || isSending) return;

    let enhanced = query;
    if (mode === 'tow') {
      enhanced = `What RV types can a ${query} tow? Include towing capacity, compatible trailer weights, and specific recommendations.`;
    } else if (mode === 'compare') {
      enhanced = `Sales comparison: ${query}. Include pros/cons of each, key differences, target buyer profiles, and sales talking points.`;
    }

    const userMsg = {
      id: `u_${Date.now()}`,
      role: 'user',
      content: query,
      mode,
      ts: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsSending(true);

    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${API}/dealer/research`,
        { query: enhanced, mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const aiMsg = {
        id: `a_${Date.now()}`,
        role: 'assistant',
        content: res.data?.response || res.data?.summary || 'No response.',
        mode,
        ts: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (err) {
      toast.error('Research failed. Please try again.');
      setMessages((prev) => [
        ...prev,
        {
          id: `e_${Date.now()}`,
          role: 'assistant',
          content: 'Sorry — that request failed. Try rephrasing or check your connection.',
          mode,
          ts: new Date().toISOString(),
          isError: true,
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const clearChat = () => {
    if (messages.length === 0) return;
    if (!window.confirm('Clear the entire chat history?')) return;
    setMessages([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    toast.success('Chat cleared');
  };

  const modes = [
    { id: 'general', label: 'General', icon: MessageSquare, color: 'blue' },
    { id: 'tow', label: 'Tow Match', icon: Car, color: 'emerald' },
    { id: 'compare', label: 'Compare', icon: Scale, color: 'purple' },
  ];

  const suggestions = [
    { label: 'Top RV brands for quality', query: 'What are the top 5 RV brands known for quality and reliability?', mode: 'general' },
    { label: 'Handle price objections', query: 'How to handle customer objections about RV prices being too high?', mode: 'general' },
    { label: 'F-150 towing capacity', query: '2024 Ford F-150', mode: 'tow' },
    { label: 'Jayco vs Grand Design', query: 'Jayco vs Grand Design travel trailers', mode: 'compare' },
  ];

  const getPlaceholder = () => {
    switch (activeMode) {
      case 'tow': return 'Enter a vehicle (e.g., 2024 Ford F-150 3.5L EcoBoost)';
      case 'compare': return 'Enter RVs to compare (e.g., Jayco Eagle vs Grand Design Reflection)';
      default: return 'Ask anything about RVs, sales techniques, or customer questions...';
    }
  };

  const formatResponse = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, idx) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <h4 key={idx} className={`font-bold text-base mt-3 mb-1.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>{line.replace(/\*\*/g, '')}</h4>;
      }
      if (line.includes('**')) {
        const parts = line.split('**');
        return (
          <p key={idx} className={`mb-1.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
            {parts.map((part, i) => i % 2 === 1 ? <strong key={i} className={isDark ? 'text-white' : 'text-slate-900'}>{part}</strong> : part)}
          </p>
        );
      }
      if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
        return (
          <div key={idx} className="flex items-start gap-2 ml-2 mb-1">
            <span className="text-blue-500 mt-1">•</span>
            <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{line.replace(/^[•-]\s*/, '')}</span>
          </div>
        );
      }
      if (line.trim()) {
        return <p key={idx} className={`mb-1.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{line}</p>;
      }
      return null;
    });
  };

  const modeColor = (m) =>
    m === 'tow' ? 'bg-emerald-500/15 text-emerald-500' :
    m === 'compare' ? 'bg-purple-500/15 text-purple-500' :
    'bg-blue-500/15 text-blue-500';

  const modeLabel = (m) => (m === 'tow' ? 'Tow Match' : m === 'compare' ? 'Comparison' : 'General');

  return (
    <div className="max-w-4xl mx-auto" data-testid="research-embed">
      {/* Header */}
      <div className={`rounded-2xl p-4 sm:p-6 mb-4 ${isDark ? 'bg-gradient-to-br from-slate-800 to-slate-900' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 sm:p-3 rounded-xl ${isDark ? 'bg-blue-500/20' : 'bg-blue-500/10'}`}>
              <Bot className="text-blue-500" size={24} />
            </div>
            <div>
              <h1 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Sales Assistant</h1>
              <p className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Rolling chat — your conversation is saved across tabs
              </p>
            </div>
          </div>
          <div className={`self-start sm:ml-auto px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
            <Zap size={12} /> AI Powered
          </div>
        </div>

        {/* Mode Selector */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {modes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setActiveMode(mode.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                activeMode === mode.id
                  ? mode.color === 'blue'
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                    : mode.color === 'emerald'
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                    : 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                  : isDark
                    ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    : 'bg-white/60 text-slate-600 hover:bg-white'
              }`}
              data-testid={`assistant-mode-${mode.id}`}
            >
              <mode.icon size={14} />
              {mode.label}
            </button>
          ))}
        </div>

        {/* Composer */}
        <div className={`flex gap-2 p-2 rounded-xl ${isDark ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={getPlaceholder()}
            className={`flex-1 border-0 shadow-none focus-visible:ring-0 text-sm sm:text-base ${isDark ? 'bg-transparent text-white placeholder:text-slate-500' : 'bg-transparent'}`}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage(input)}
            data-testid="assistant-input"
          />
          <Button
            onClick={() => sendMessage(input)}
            disabled={isSending || !input.trim()}
            size="lg"
            className={`px-4 sm:px-6 ${
              activeMode === 'tow' ? 'bg-emerald-500 hover:bg-emerald-600' :
              activeMode === 'compare' ? 'bg-purple-500 hover:bg-purple-600' :
              'bg-blue-500 hover:bg-blue-600'
            }`}
            data-testid="assistant-send-btn"
          >
            {isSending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
          </Button>
        </div>
      </div>

      {/* Empty state — quick suggestions */}
      {messages.length === 0 && !isSending && (
        <div className="mb-4">
          <p className={`text-xs font-medium mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>QUICK SUGGESTIONS</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {suggestions.map((item, idx) => (
              <button
                key={idx}
                onClick={() => { setActiveMode(item.mode); sendMessage(item.query, item.mode); }}
                className={`flex items-center justify-between p-3 rounded-xl text-left text-sm transition-all group ${
                  isDark
                    ? 'bg-slate-800/50 hover:bg-slate-800 text-slate-300'
                    : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`assistant-suggestion-${idx}`}
              >
                <span>{item.label}</span>
                <ArrowRight size={14} className={`opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Rolling chat thread */}
      {messages.length > 0 && (
        <div className={`rounded-2xl ${isDark ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'} overflow-hidden`} data-testid="assistant-thread">
          {/* Thread header */}
          <div className={`flex items-center justify-between px-4 py-2.5 border-b ${isDark ? 'border-slate-800 bg-slate-800/40' : 'border-slate-100 bg-slate-50'}`}>
            <span className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {messages.length} message{messages.length === 1 ? '' : 's'} in this chat
            </span>
            <button
              onClick={clearChat}
              className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${
                isDark ? 'text-slate-400 hover:text-red-400 hover:bg-slate-700/60' : 'text-slate-500 hover:text-red-600 hover:bg-red-50'
              }`}
              data-testid="assistant-clear-btn"
            >
              <Trash2 size={12} /> Clear
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto p-4 sm:p-5 space-y-4">
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`assistant-msg-${m.role}`}>
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  m.role === 'user'
                    ? (isDark ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700')
                    : 'bg-blue-500 text-white'
                }`}>
                  {m.role === 'user' ? <User2 size={14} /> : <Bot size={14} />}
                </div>
                <div className={`flex-1 max-w-[85%] ${m.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block text-left rounded-2xl px-4 py-3 ${
                    m.role === 'user'
                      ? (isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                      : m.isError
                        ? (isDark ? 'bg-red-900/30 text-red-200 border border-red-800' : 'bg-red-50 text-red-800 border border-red-200')
                        : (isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-800')
                  }`}>
                    {m.role === 'user' ? (
                      <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                    ) : (
                      <div className="prose prose-sm max-w-none">{formatResponse(m.content)}</div>
                    )}
                  </div>
                  {m.mode && m.mode !== 'general' && (
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${modeColor(m.mode)}`}>
                      {modeLabel(m.mode)}
                    </span>
                  )}
                </div>
              </div>
            ))}

            {isSending && (
              <div className="flex gap-3" data-testid="assistant-typing">
                <div className="shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center">
                  <Bot size={14} />
                </div>
                <div className={`rounded-2xl px-4 py-3 ${isDark ? 'bg-slate-800' : 'bg-slate-100'} flex items-center gap-2`}>
                  <Loader2 className="animate-spin text-blue-500" size={14} />
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Researching…</span>
                  <Sparkles className="text-amber-400 animate-pulse" size={12} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebResearchEmbed;
